import { type FormatNumberOptions, formatNumber } from "@shared/lib/formatNumber";
import { useMemo } from "react";

export interface NumberFormatterProps
	extends FormatNumberOptions,
	Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
	/**
	 * Element type to render
	 * @default "span"
	 */
	as?: React.ElementType;
}

export const NumberFormatter = ({
	value,
	prefix,
	suffix,
	useGrouping,
	decimalScale,
	fixedDecimalScale,
	allowNegative,
	locale,
	emptyValue,
	numberStyle,
	currency,
	percentInputScale,
	as: As = "span",
	...rest
}: NumberFormatterProps) => {
	const displayValue = useMemo(
		() =>
			formatNumber({
				value,
				prefix,
				suffix,
				useGrouping,
				decimalScale,
				fixedDecimalScale,
				allowNegative,
				locale,
				emptyValue,
				numberStyle,
				currency,
				percentInputScale,
			}),
		[
			value,
			prefix,
			suffix,
			useGrouping,
			decimalScale,
			fixedDecimalScale,
			allowNegative,
			locale,
			emptyValue,
			numberStyle,
			currency,
			percentInputScale,
		],
	);

	return <As {...rest}>{displayValue}</As>;
};
NumberFormatter.displayName = "NumberFormatter";

NumberFormatter.Currency = (({
	numberStyle = "currency",
	currency = "HUF",
	decimalScale = 0,
	useGrouping = true,
	...rest
}: NumberFormatterProps) => {
	return (
		<NumberFormatter
			numberStyle={numberStyle}
			currency={currency}
			decimalScale={decimalScale}
			useGrouping={useGrouping}
			{...rest}
		/>
	);
}) as React.FC<NumberFormatterProps>;
NumberFormatter.Currency.displayName = "NumberFormatter.Currency";

NumberFormatter.Percent = (({ numberStyle = "percent", decimalScale = 2, ...rest }: NumberFormatterProps) => {
	return <NumberFormatter numberStyle={numberStyle} decimalScale={decimalScale} {...rest} />;
}) as React.FC<NumberFormatterProps>;
NumberFormatter.Percent.displayName = "NumberFormatter.Percent";
